import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle, Eye } from "lucide-react";
import { SITE_CONFIG, getWhatsAppLink, getTelLink, getMailLink } from "../lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VISIT_KEY = "energia_vasta_session_tracked";

export const Footer = () => {
  const [visitCount, setVisitCount] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const track = async () => {
      try {
        const alreadyTracked = sessionStorage.getItem(VISIT_KEY);
        let res;
        if (alreadyTracked) {
          res = await axios.get(`${API}/visits/count`);
        } else {
          res = await axios.post(`${API}/visits/track`);
          sessionStorage.setItem(VISIT_KEY, "1");
        }
        if (!cancelled) setVisitCount(res.data?.count ?? 0);
      } catch (e) {
        if (!cancelled) setVisitCount(null);
      }
    };

    track();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-slate-950 border-t border-emerald-500/10 pt-20 pb-8 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <div className="grid lg:grid-cols-4 gap-12 mb-12">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img
                src={SITE_CONFIG.logoUrl}
                alt="Logo Dott. Ing. Vasta Ivan"
                className="h-14 w-14 object-contain"
              />
              <div className="leading-tight">
                <div className="text-[10px] font-semibold tracking-[0.2em] text-emerald-400 uppercase">
                  Dott. Ing.
                </div>
                <div className="text-base font-bold text-white">Vasta Ivan</div>
              </div>
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              Servizi di ingegneria fotovoltaica e Conto Termico in tutta Italia. Oltre 10 anni di
              esperienza al servizio dei tuoi progetti.
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-5 uppercase tracking-wider">
              Naviga
            </h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-slate-400 hover:text-emerald-400 transition">Home</Link></li>
              <li><Link to="/chi-siamo" className="text-slate-400 hover:text-emerald-400 transition">Chi Siamo</Link></li>
              <li><Link to="/servizi" className="text-slate-400 hover:text-emerald-400 transition">Servizi</Link></li>
              <li><Link to="/consulenza" className="text-slate-400 hover:text-emerald-400 transition">Consulenza</Link></li>
              <li><Link to="/contatti" className="text-slate-400 hover:text-emerald-400 transition">Contatti</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-5 uppercase tracking-wider">
              Servizi
            </h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li>Progettazione Impianti Fotovoltaici</li>
              <li>Pratiche GSE & E-Distribuzione</li>
              <li>Conto Termico 2.0 e 3.0</li>
              <li>Progettazione Elettrica</li>
              <li>Consulenza Energetica</li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-white mb-5 uppercase tracking-wider">
              Contatti
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition group"
                >
                  <MessageCircle className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                  <span>WhatsApp: {SITE_CONFIG.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={getTelLink()}
                  data-testid="footer-tel"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                  <span>{SITE_CONFIG.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={getMailLink()}
                  data-testid="footer-email"
                  className="flex items-start gap-3 text-slate-400 hover:text-emerald-400 transition break-all"
                >
                  <Mail className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                  <span>{SITE_CONFIG.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" />
                <span>Sede: {SITE_CONFIG.city}<br/>{SITE_CONFIG.coverage}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Visit counter */}
        <div
          data-testid="visit-counter-row"
          className="flex justify-center mb-6"
        >
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-emerald-500/5 border border-emerald-400/20">
            <Eye className="w-4 h-4 text-emerald-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
              Visite al sito
            </span>
            <span
              data-testid="visit-counter-value"
              className="text-sm font-bold text-emerald-300 tabular-nums"
            >
              {visitCount === null ? "—" : visitCount.toLocaleString("it-IT")}
            </span>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © {new Date().getFullYear()} {SITE_CONFIG.fullName}. Tutti i diritti riservati.
          </p>
          <p className="text-xs text-slate-500">
            Sede legale: {SITE_CONFIG.city} — P.IVA su richiesta
          </p>
        </div>

        {/* SIMOMARKETING credit */}
        <div className="mt-6 pt-6 border-t border-white/5 text-center">
          <p
            data-testid="simomarketing-credit"
            className="text-xs text-slate-500"
          >
            Sito realizzato da{" "}
            <a
              href="https://simomarketing.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="simomarketing-link"
              className="font-bold text-emerald-400 hover:text-emerald-300 hover:underline transition"
            >
              SIMOMARKETING
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};
